import React from 'react';
import styled from 'styled-components';
import {Alert, DatePicker} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {formatQuoteDate} from '../quoteDocument';
import type {QuoteDocumentData} from '../resolver/types';

const Facts = styled.div`
	display: grid;
	grid-template-columns: auto 1fr;
	/* A fixed row height, so the one row carrying a date picker does not tower over the rest. */
	grid-auto-rows: 44px;
	gap: 8px 20px;
	align-items: center;
	padding: 16px 14px;
	margin-bottom: 8px;
	border: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	border-radius: 4px;
`;

const FactLabel = styled.span`
	${({theme}) => theme.sys.font.detail[1]}
	color: ${({theme}) => theme.sys.color.text.content.auxiliary};
`;

const FactValue = styled.span`
	${({theme}) => theme.sys.font.body[2]}
	text-align: right;
`;

const PickerCell = styled.span`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	min-width: 0;
`;

/*
 * A definite box for the picker, because right-aligning the cell alone does not move it: the
 * picker's own wrapper is a flex:1 column, so it fills the cell and its control sits at the left
 * edge while every other value in the grid reads right-aligned. Overflow runs to the left, so the
 * right edge stays on the column whatever the control's intrinsic width turns out to be.
 */
const PickerBox = styled.span`
	display: flex;
	justify-content: flex-end;
	flex: none;
	width: 184px;
`;

type Props = {
	data: QuoteDocumentData;
	/** ISO date (yyyy-mm-dd). */
	expiryDate: string;
	/** ISO date; the day after issue, so a quote cannot expire before it is sent. */
	minExpiryDate: string;
	onExpiryChange: (iso: string) => void;
	/** Set when the picker is spelling a date the quote cannot carry, so nothing has been taken. */
	expiryError?: string;
};

/**
 * Everything on the cover is derived from the project, the company and the publish itself, so it
 * is read-only here — except Valid Until, which the user sets per quote.
 *
 * The expiry needs no contract change: it is already a CreateQuote input and a real column. It is
 * deliberately absent from the stored design payload, because a stored expiry would reopen in the
 * past on a revise.
 */
const CoverDetailsBlock: React.FC<Props> = ({data, expiryDate, minExpiryDate, onExpiryChange, expiryError}) => {
	const intl = useIntl();
	const preparedFor = data.clientName || data.projectName;

	return (
		<>
			<Facts>
				<FactLabel>{intl.formatMessage({id: 'quote_designer.cover.project'})}</FactLabel>
				<FactValue>{`${data.projectIndicator} · ${data.projectName}`}</FactValue>

				<FactLabel>{intl.formatMessage({id: 'quote_designer.cover.version'})}</FactLabel>
				<FactValue>{`v${data.revision}`}</FactValue>

				<FactLabel>{intl.formatMessage({id: 'quote_designer.cover.prepared_by'})}</FactLabel>
				<FactValue>{data.companyName}</FactValue>

				<FactLabel>{intl.formatMessage({id: 'quote_designer.cover.prepared_for'})}</FactLabel>
				<FactValue>{preparedFor}</FactValue>

				<FactLabel>{intl.formatMessage({id: 'quote_designer.cover.quote_date'})}</FactLabel>
				<FactValue>{formatQuoteDate(data.issuedDate, intl)}</FactValue>

				<FactLabel>{intl.formatMessage({id: 'quote_designer.cover.valid_until'})}</FactLabel>
				<PickerCell>
					{/* The picker speaks ISO in both directions, which is what the template and the
					    publish mutation carry — so no date library sits between them. */}
					{/* The row's own label is the visible one; naming the picker's `label` too rendered
					    "Valid until" twice. aria-label is what the design system documents for a
					    picker shown without a visible label of its own. */}
					<PickerBox>
						<DatePicker
							value={expiryDate}
							minValue={minExpiryDate}
							onChange={onExpiryChange}
							aria-label={intl.formatMessage({id: 'quote_designer.cover.valid_until'})}
							errorText={expiryError}
							data-cy="quote-expiry-date"
						/>
					</PickerBox>
				</PickerCell>
			</Facts>
			<Alert type={Alert.TYPE.INFORMATION} text={intl.formatMessage({id: 'quote_designer.cover.description'})} />
		</>
	);
};

export default CoverDetailsBlock;
